import React, { useCallback, useMemo, useState } from 'react';
import { Select } from 'antd';
import Typography from 'antd/lib/typography';
import { useTranslation } from 'react-i18next';
import { IDevice } from '@pages/Assets/Inventory/types';
import useSwr from '@hooks/useSwr';
import ActionTypes from '@utils/deviceActions';
import { IDeviceActionRequestPayload } from '../types';

interface IUActionRestriction {
  actionKey: number;
  devicesProps: IDevice[] | undefined;
  uploadedDevices: any;
  currentStep: number;
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
}

const { Paragraph } = Typography;

const useActionRestriction = ({
  handleDeviceActions,
  actionKey,
  devicesProps,
  uploadedDevices,
  currentStep,
}: IUActionRestriction) => {
  const [selectedRestriction, setSelectedRestriction] = useState<
    string | undefined
  >(undefined);
  const { t: translate } = useTranslation(['device-actions-modal']);

  const { data: dataRestriction } = useSwr<string[]>(
    actionKey === ActionTypes.OPERATOR_RESTRICTION
      ? '/service-proxy/hlr-operators'
      : null,
  );

  const restrictionOptions = useMemo(
    () =>
      dataRestriction
        ?.filter(operator => !operator.includes('TODAS OPERADORAS'))
        ?.map(operator => ({
          label: operator,
          value: operator,
        })),
    [dataRestriction],
  );

  const contentSteps = useMemo(() => {
    return [
      {
        title: translate('steps.operator-restriction.name'),
        content: (
          <div className="action-operator-restriction">
            <Paragraph style={{ padding: '8px 0' }}>
              {translate('select-operator-restriction')}
            </Paragraph>
            <Select
              onChange={(value: string) => {
                setSelectedRestriction(value);
              }}
              value={selectedRestriction}
              options={restrictionOptions}
            />
          </div>
        ),
      },
    ];
  }, [restrictionOptions, selectedRestriction, translate]);

  const reinitState = () => {
    setSelectedRestriction(undefined);
  };

  const nextStepIsDisabled = useCallback(() => {
    if (devicesProps && devicesProps?.length > 0) {
      return currentStep === 0 && !selectedRestriction;
    }
    if (devicesProps?.length === 0) {
      return currentStep === 1 && !selectedRestriction;
    }

    return false;
  }, [currentStep, selectedRestriction, devicesProps]);

  const onSubmit = useCallback(async () => {
    const payload: IDeviceActionRequestPayload = {};
    payload.action = actionKey;

    payload.devices =
      devicesProps && devicesProps?.length > 0
        ? devicesProps?.map(device => ({
            msisdn: device.msisdn,
            iccid: device.iccid,
          }))
        : uploadedDevices;
    payload.operatorRestriction = selectedRestriction;
    return handleDeviceActions(payload);
  }, [
    actionKey,
    devicesProps,
    selectedRestriction,
    uploadedDevices,
    handleDeviceActions,
  ]);

  const restrictionData = {
    contentSteps,
    onSubmit,
    reinitState,
    nextStepIsDisabled,
  };

  return { restrictionData };
};

export default useActionRestriction;
